import { useState } from 'react';
import { useScheduleC } from '@/api/hooks/useScheduleC';
import { useScheduleCExplanation } from '@/api/hooks/useScheduleCExplanation';
import { ScheduleCLineTable } from './ScheduleCLineTable';
import { TotalExplanationRail } from './TotalExplanationRail';
import { PreparerCalculationsCard } from './PreparerCalculationsCard';

export function ScheduleCPage({ grantId, taxYear }: { grantId: string; taxYear: number }) {
  const query = useScheduleC(grantId, taxYear);
  const [explainedLine, setExplainedLine] = useState<string | null>(null);
  const explanation = useScheduleCExplanation(grantId, taxYear, explainedLine);

  if (query.isLoading) return <div aria-label="Loading Schedule C" className="mx-auto w-full max-w-6xl p-4"><div className="h-8 w-56 animate-pulse rounded bg-muted" /><div className="mt-4 h-64 animate-pulse rounded bg-muted" /></div>;
  if (query.isError || !query.data) return <div className="mx-auto w-full max-w-6xl p-4"><h2 className="text-title font-semibold">Schedule C {taxYear}</h2><p role="alert" className="mt-3">Schedule C is unavailable.</p><button type="button" onClick={() => void query.refetch?.()} className="mt-3 min-h-11 rounded-md border px-3 py-2">Retry</button></div>;

  const data = query.data;
  const firstLine = data.lines[0]?.tax_line ?? null;
  const preparerCount = data.lines.filter((line) => line.preparer_required).length;
  const explain = (taxLine = firstLine) => { if (taxLine) setExplainedLine(taxLine); };
  const locked = data.year_lock.locked;
  return (
    <div className="mx-auto w-full max-w-6xl p-4" aria-labelledby="schedule-c-heading">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 id="schedule-c-heading" tabIndex={-1} className="text-title font-semibold">Schedule C {taxYear}</h2><p className="text-sm text-muted-foreground">Taxonomy {data.taxonomy_version} · {preparerCount} lines need the preparer · every total drills to rows</p></div>
        {locked && <span className="rounded-full bg-foreground px-3 py-1 text-xs text-background">Filed · locked</span>}
      </header>
      {data.income_status === 'unverified' && <p role="status" className="mt-3 text-sm text-warning-foreground">Income unverified · no confirmed 1099 tie-out.</p>}
      {locked && <p role="status" className="mt-3 rounded border border-border p-3 text-sm text-muted-foreground">Schedule C is read-only for this filed year.</p>}
      {data.status === 'insufficient' && <p role="status" className="mt-4 rounded border border-border p-3 text-sm text-muted-foreground">Mixed currency data is insufficient{data.excluded_count == null ? '.' : ` · ${data.excluded_count} rows excluded.`}</p>}
      {data.status === 'empty' && <p role="status" className="mt-4 rounded border border-border p-3 text-sm text-muted-foreground">No business rows shared yet.</p>}
      <div className="mt-4 grid gap-4 desktop:grid-cols-[minmax(0,1fr)_360px]">
        <section aria-label="Schedule C lines">
          <ScheduleCLineTable lines={data.lines} computedLine28={data.computed_line_28} />
          <PreparerCalculationsCard lines={data.lines} />
          <p className="mt-3 text-xs text-muted-foreground">Line 31 net profit is withheld until the preparer enters lines 9, 13, and 30. Records only. Not tax advice.</p>
        </section>
        <aside className="rounded-[10px] border border-border bg-card p-4">
          <button type="button" onClick={() => explain()} disabled={firstLine === null} className="min-h-11 rounded-md border border-border px-3 py-2 text-sm font-semibold">Explain this total</button>
          <TotalExplanationRail taxLine={explainedLine} explanation={explanation} />
        </aside>
      </div>
    </div>
  );
}

export default ScheduleCPage;
